import React from 'react';
import Drawer from '../ui/Drawer';
import Badge from '../ui/Badge';
import { categoryLabel, difficultyMeta } from './constants';

/**
 * QuestionDetailDrawer - read-only detail view with optional edit/archive actions.
 *
 * Props:
 *   open, onClose
 *   question
 *   canEdit    - boolean
 *   onEdit     - () => void
 *   onArchive  - () => void
 */
export default function QuestionDetailDrawer({
  open, onClose, question, canEdit, onEdit, onArchive,
}) {
  if (!question) return null;
  const diff = difficultyMeta(question.difficulty);
  const tone = diff.tone === 'success' ? 'green'
             : diff.tone === 'warning' ? 'amber'
             : diff.tone === 'danger'  ? 'red' : 'slate';

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={question.title}
      width="lg"
      footer={canEdit ? (
        <div className="flex items-center justify-between">
          <button
            onClick={onArchive}
            className="px-3 py-1.5 text-xs rounded-lg border border-red-200 text-red-700
                       hover:bg-red-50"
          >
            {question.active ? 'Archive' : 'Archived'}
          </button>
          <button
            onClick={onEdit}
            className="btn btn-sm btn-primary"
          >
            Edit
          </button>
        </div>
      ) : null}
    >
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <Badge label={categoryLabel(question.category)} color="teal" size="xs" />
          <Badge label={diff.label} color={tone} size="xs" />
          {question.timeEstimateMinutes != null && (
            <Badge label={`${question.timeEstimateMinutes} min`} color="slate" size="xs" />
          )}
          {question.tags?.map((t) => (
            <Badge key={t} label={`#${t}`} color="slate" size="xs" />
          ))}
        </div>

        {question.description && (
          <Section title="Description">
            <p className="whitespace-pre-wrap">{question.description}</p>
          </Section>
        )}

        {question.hints?.length > 0 && (
          <Section title="Hints">
            <ol className="list-decimal pl-5 space-y-1">
              {question.hints.map((h, i) => <li key={i}>{h}</li>)}
            </ol>
          </Section>
        )}

        {question.sampleAnswer ? (
          <Section title="Sample answer">
            <p className="whitespace-pre-wrap">{question.sampleAnswer}</p>
          </Section>
        ) : (
          <Section title="Sample answer">
            <p className="italic text-slate-400">
              Hidden - only visible to members of the owning organization.
            </p>
          </Section>
        )}

        <p className="text-[11px] text-slate-400 pt-2 border-t border-slate-100">
          Created by {question.createdBy || 'unknown'} ·{' '}
          {question.createdAt ? new Date(question.createdAt).toLocaleString() : ''}
        </p>
      </div>
    </Drawer>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">
        {title}
      </h4>
      <div className="text-sm text-slate-700">{children}</div>
    </div>
  );
}
